import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { Checkbox, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { colors, calculateIsMinor } from '../constants';
import {
  StepTitle, FormSection, FormItem, ErrorText, InfoBox, LargeInput
} from '../styledComponents';

const { Paragraph } = Typography;

const StepConsents = ({ values, errors, touched, setFieldValue }) => {
  const isMinor = calculateIsMinor(values.dateOfBirth);
  
  return (
    <>
      <StepTitle level={4}>BHA Waiver & Consent Form</StepTitle>
      <InfoBox>
        <div className="icon"><InfoCircleOutlined /></div>
        <div className="content">
          <Paragraph style={{ margin: 0, color: colors.text }}>
            Please review and acknowledge the following consents. These are required to process your Lp(a) test request.
          </Paragraph>
        </div>
      </InfoBox>
      
      <FormSection>
        <FormItem>
          <div style={{ backgroundColor: colors.accent, padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
              I understand that as part of the screening process, a small sample of blood will be collected 
              either by venipuncture or finger prick.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
              <strong>Risks Acknowledgment:</strong> I am aware of the associated risks, which include but are not limited to:
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Bleeding</li>
                <li>Vessel injury</li>
                <li>Bruising</li>
                <li>Infection</li>
              </ul>
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
              <strong>Disclaimer of Relationship:</strong> I acknowledge that participation does not create a patient/doctor 
              relationship with BHA, its affiliates, or any healthcare providers involved.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
              <strong>Emergency Authorization:</strong> In case of an emergency, I authorize BHA, its affiliates, or healthcare 
              providers to arrange for necessary medical treatment. I agree to hold harmless all parties involved in the 
              administration of such emergency care. I understand that I am responsible for any medical expenses related to 
              the emergency treatments provided.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
              <strong>Minor Consent:</strong> For participants under the age of 18, a signature from a parent or guardian is required.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', color: colors.text }}>
              <strong>Communication Consent:</strong> By signing this form, I consent to receive periodic newsletters from BHA. 
              I understand that these communications are part of my engagement with BHA and I can opt-out at any time.
            </Paragraph>
          </div>
          <Checkbox
            checked={values.procedureRisksAcknowledgement}
            onChange={(e) => {
              const isChecked = e.target.checked;
              setFieldValue('procedureRisksAcknowledgement', isChecked);
              setFieldValue('emergencyAuthorization', isChecked);
              setFieldValue('communicationConsent', isChecked);
            }}
            style={{ 
              lineHeight: '1.5',
              color: touched.procedureRisksAcknowledgement && errors.procedureRisksAcknowledgement ? colors.error : colors.text 
            }}
          >
            By typing my name below, I provide my consent for BHA to perform the screening as described above and waive any liability against BHA and their partners for the conduct of the screening
          </Checkbox>
          <ErrorMessage name="procedureRisksAcknowledgement" component={ErrorText} />
        </FormItem>
      </FormSection>
      {isMinor && (
        <FormSection>
          <FormItem>
            <div style={{ backgroundColor: colors.accent, padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <Paragraph style={{ fontSize: '14px', color: colors.text }}>
                <strong>Minor Consent:</strong> As this individual is under 18 years of age, a parent or legal guardian must
                consent to this test on their behalf.
              </Paragraph>
            </div>
            <Checkbox
              checked={values.minorConsent}
              onChange={(e) => setFieldValue('minorConsent', e.target.checked)}
              style={{ 
                lineHeight: '1.5',
                color: touched.minorConsent && errors.minorConsent ? colors.error : colors.text 
              }}
            >
              I am the parent or legal guardian of this minor and consent to this test
            </Checkbox>
            <ErrorMessage name="minorConsent" component={ErrorText} />
            
            {values.minorConsent && (
              <div style={{ marginTop: '16px' }}>
                <Field name="parentGuardianName">
                  {({ field }) => (
                    <LargeInput
                      {...field}
                      placeholder="Parent/Guardian Full Name"
                      style={{ 
                        borderColor: touched.parentGuardianName && errors.parentGuardianName ? colors.error : null 
                      }}
                    />
                  )}
                </Field>
                <ErrorMessage name="parentGuardianName" component={ErrorText} />
              </div>
            )}
          </FormItem>
        </FormSection>
      )}
    </>
  );
};

export default StepConsents;
